// AR Overseas Exports — Sectors, product groups and varieties

export const sectors = [
  {
    slug: "pulses-grains",
    index: "03",
    name: "Pulses & Grains",
    tagline: "Cereals, legumes, oil seeds & grain derivatives",
    icon: "Wheat",
    image: "https://images.pexels.com/photos/36346840/pexels-photo-36346840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800",
    hero: "https://images.pexels.com/photos/36346840/pexels-photo-36346840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200",
    stats: { countries: "40+", products: "60+" },
    blurb: "From the world's longest Basmati grain to volume-driving IR64, our rice desk supplies millers, retailers, and food programs across six continents. Complete pulses, oilseeds and processed grain portfolio — including premium organic and GI-tagged lines for discerning buyers.",
    highlights: [
      "Pusa Basmati 1121 — up to 8.4mm grain, 24mm cooked elongation",
      "Sella / Parboiled rice in 25kg, 50kg and bulk",
      "GI-tagged regional specialties — Gobindobhog, Kalanamak, Joha",
      "Full aflatoxin + pesticide residue testing per destination spec",
      "Organic-certified streams available for EU, USDA NOP & JAS markets",
    ],
    groups: [
      {
        title: "Premium Basmati Varieties",
        items: [
          { name: "Pusa Basmati 1121", desc: "World's longest grain; up to 8.4mm raw, 24mm cooked. The global workhorse of Indian Basmati exports — rich aroma, non-sticky finish, ideal for hotels and retail brands." },
          { name: "Pusa Basmati 1509", desc: "Premium hybrid, quick cooking time, excellent elongation — economical alternative to 1121. Highly preferred for large retail and catering contracts." },
          { name: "1401 Basmati", desc: "Improved aromatic, consistent cooking quality. Reliable for bulk catering and retail chains." },
          { name: "1718 Basmati", desc: "High-yield, disease-resistant, premium aroma. Reliable for large-scale supply contracts." },
          { name: "Traditional / Classic Basmati", desc: "Dehradun & Punjab heritage lines — gourmet retailers, fine-dining restaurants and specialty buyers." },
          { name: "Sava Kanak 2211", desc: "Specialty amber-bold grain, high nutrient density." },
        ],
      },
      {
        title: "Aromatic & GI-Tagged Regional Rice",
        items: [
          { name: "Gobindobhog (West Bengal)", desc: "Short-grain, buttery, sweet aroma. Premium kheer, bhog & religious offerings — GI-tagged, traceable origin." },
          { name: "Kalanamak (Uttar Pradesh)", desc: "'Buddha Rice' — distinctive black husk, intense fragrance. UNESCO-recognised heritage variety with organic cultivation practices." },
          { name: "Joha (Assam)", desc: "Delicate aromatic, high nutritional profile. Prized by gourmet importers and ethnic food specialists." },
          { name: "Seeraga Samba (Tamil Nadu)", desc: "Tiny grain essential for authentic South Asian biryani — irreplaceable aromatic profile." },
          { name: "Ambemohar (Maharashtra)", desc: "Short-grain with mango-blossom fragrance. Popular in premium organic and ethnic food segments." },
          { name: "Jeerakasala / Gandhakasala (Kerala)", desc: "Scented varieties for Malabar-style rice dishes — growing demand from health-food importers." },
        ],
      },
      {
        title: "Non-Basmati Export Varieties",
        items: [
          { name: "IR64 Long Grain", desc: "Most widely exported non-basmati — affordable, bulk-shipping durable, consistent quality. Top choice for food-aid programs and mass-market retail." },
          { name: "Sona Masoori", desc: "Lightweight medium grain — low glycaemic index, daily-use staple for Indian diaspora and health-conscious consumers globally." },
          { name: "Swarna (IR36)", desc: "Short–medium grain, food security programs. High-yield variety suited for institutional supply and food relief." },
          { name: "Ponni Rice", desc: "Soft medium-grain — popular for its easy digestibility and mild flavour across multiple cuisines." },
          { name: "Sharbati Rice", desc: "'Affordable Basmati' — mild aroma, cost-effective alternative to premium varieties." },
          { name: "Parboiled (Sella) Rice", desc: "Nutritionally superior, firm texture — retains more B vitamins than white rice. Strong demand across African markets." },
          { name: "Brown Rice / Whole Grain", desc: "Bran layer intact, fibre-rich. Strong growth segment in health-oriented retail across Western markets." },
          { name: "Broken Rice", desc: "Industrial, brewing and affordable-staple segments. Cost-efficient source of starch and feed-quality grain." },
        ],
      },
      {
        title: "Specialty & Ayurvedic Rice",
        items: [
          { name: "Chakhao Black Rice", desc: "Antioxidant-rich anthocyanin grain — premium health food gaining traction in European and North American specialty stores." },
          { name: "Matta (Kerala Red Rice)", desc: "Nutrient-rich with bran layer intact, nutty flavour. Beloved by health-food importers and Ayurveda enthusiasts." },
          { name: "Navara Rice", desc: "Ancient medicinal variety used in Ayurvedic treatments. Rare, small-batch supply with phytosanitary documentation." },
          { name: "Kinmemai-style Premium Polish", desc: "Advanced polishing processes available on request for luxury rice market positioning." },
        ],
      },
      {
        title: "🌿 Premium & Organic Category",
        items: [
          { name: "USDA NOP Certified Organic Basmati", desc: "USDA National Organic Program certified. No synthetic pesticides. COA + residue-test report included. Ideal for USA, EU and Japanese premium-organic importers." },
          { name: "EU Organic Basmati (EC 848/2018)", desc: "EU Organic compliant. Full traceability from farm to port. Accredited third-party organic inspection available." },
          { name: "JAS Organic Rice (Japan)", desc: "Japanese Agricultural Standard organic certification stream. Test reports per MAFF requirements." },
          { name: "Biodynamic / Demeter Rice", desc: "Small-batch biodynamic cultivation available. Premium positioning for Demeter-certified retail partners." },
          { name: "Low-GI / Diabetic-Friendly Varieties", desc: "Verified glycaemic-index profile. Packaging and claims support for functional-food retailers and health-category buyers." },
          { name: "Ancient / Heritage Grain Assortment", desc: "Curated heritage rice collection — custom blending and private-label programmes for gourmet food brands." },
        ],
      },
      {
        title: "Pulses & Legumes",
        items: [
          { name: "Chickpeas (Desi & Kabuli)", desc: "Garbanzo beans — whole & split, premium bold calibration. Leading protein crop for plant-based food manufacturers." },
          { name: "Pigeon Peas (Toor / Arhar)", desc: "Whole and split Toor Dal — high-protein staple with strong demand across South Asian diaspora markets." },
          { name: "Lentils (Masoor)", desc: "Red lentils whole & split — fast cooking, high protein. Popular with health-food buyers and food-aid organisations." },
          { name: "Green Gram (Moong)", desc: "Whole, split & sprouted — Asian households and plant-based food market." },
          { name: "Black Gram (Urad)", desc: "Black matpe whole & split — South Indian staple, rising demand in functional food products." },
          { name: "Kidney Beans (Rajma)", desc: "Red, light red & dark red kidney beans. Reliable supply for retail, food service and processing." },
          { name: "Moth Beans", desc: "Sprouted and whole — specialty pulse for ethnic food and health stores." },
          { name: "Horse Gram (Kulthi)", desc: "Traditional South Asian protein pulse — growing international interest in superfoods." },
          { name: "Black Eyed Peas (Lobia)", desc: "Export grade, machine cleaned — steady demand across African and Caribbean markets." },
        ],
      },
      {
        title: "Cereals & Oilseeds",
        items: [
          { name: "Wheat & Meslin", desc: "Durum, hard & soft varieties for milling, food processing and baking industries." },
          { name: "Maize (Corn)", desc: "Feed & food grade, yellow and white. High demand for starch, ethanol and feed industries." },
          { name: "Barley, Oats, Rye, Millet, Sorghum, Buckwheat", desc: "Full cereal portfolio — gluten-free options (millet, sorghum, buckwheat) for health markets." },
          { name: "Soya Beans, Groundnut, Sesame, Sunflower, Castor, Linseed, Rape/Colza", desc: "Premium oilseeds for edible oil, protein meal and industrial end-uses." },
          { name: "Flours, Groats, Malt, Starches, Wheat Gluten", desc: "Processed grain products — private label and co-manufacturing options available." },
        ],
      },
    ],
  },
  {
    slug: "organic-fertilizers",
    index: "01",
    name: "Organic Fertilizers",
    tagline: "Bio-based, natural & sustainable soil enrichment",
    icon: "Leaf",
    image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=800&q=80",
    hero: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1600&q=80",
    stats: { countries: "28+", products: "40+" },
    blurb: "Natural, animal-based, plant-based and bio-fertilizer formulations — EU Organic, USDA NOP & international compliant streams available. As global agriculture pivots toward sustainable inputs, AR Overseas supplies certified organic fertilisers backed by rigorous testing, full traceability and export-ready documentation.",
    highlights: [
      "EU Organic (EC 848/2018) · USDA NOP · GlobalG.A.P. compatible",
      "Bulk, granulated, liquid & custom-bagged formats",
      "Vegan & halal-certified streams",
      "Heavy metal & pathogen testing reports included",
      "Compatible with drip, broadcast, foliar & fertigation systems",
    ],
    groups: [
      {
        title: "Natural & Animal Based",
        items: [
          { name: "Cow Dung Cake", desc: "Sun-dried, odour-free, stackable. Traditional slow-release nitrogen source — excellent for soil conditioning and microbial activation." },
          { name: "Vermicompost", desc: "Earthworm-processed, NPK-balanced. Rich in beneficial microbes, enzymes and plant-available nutrients. Premium-grade with consistent particle size." },
          { name: "Farmyard Manure (FYM)", desc: "Fully composted, screened and pathogen-tested. Broad-spectrum organic matter for large-scale farm applications." },
          { name: "Bone Meal / Blood Meal / Fish Meal", desc: "High phosphorus & nitrogen inputs. Fast-acting organic nitrogen for high-value crops and horticulture." },
          { name: "Fish Emulsion / Fish Hydrolysate", desc: "Liquid protein fertilizer with trace minerals. Excellent for foliar and drip irrigation applications." },
          { name: "Poultry Manure (Pelletised)", desc: "High nitrogen content, pelletised for easy handling and precise application." },
        ],
      },
      {
        title: "Plant-Based & Oil Cakes",
        items: [
          { name: "Neem Cake", desc: "Dual-purpose fertilizer & natural pest repellent. Neem limonoids inhibit soil-borne pests. USDA NOP and EU Organic compatible." },
          { name: "Castor, Groundnut, Karanj (Pongamia) Cakes", desc: "Slow-release nitrogen sources with additional soil-conditioning benefits." },
          { name: "Soybean Meal", desc: "48%+ protein; feed & fertilizer grade. High N content for intensive cultivation." },
          { name: "Seaweed / Kelp Extract", desc: "Liquid & flake; rich in cytokinins, alginates and trace minerals. Proven plant growth enhancement." },
          { name: "Cocopeat Bricks & Potting Mix", desc: "EC-washed, sterilised. Excellent water-retention growing medium for horticulture and hydroponics." },
          { name: "Mustard Cake", desc: "Slow-release nitrogen and sulphur. Widely used in tea gardens and vegetable cultivation." },
        ],
      },
      {
        title: "Bio-Based & Advanced",
        items: [
          { name: "Rhizobium & Azotobacter Inoculants", desc: "Nitrogen-fixing biofertilizers — can reduce synthetic N requirement by 20–40% in leguminous crops." },
          { name: "Mycorrhizal Inoculants", desc: "Root-symbiont growth boosters — enhanced phosphorus uptake, drought tolerance and root development." },
          { name: "Humic & Fulvic Acid (Liquid / Granular)", desc: "Concentrated soil conditioners. Improve CEC, water retention and nutrient bioavailability." },
          { name: "Bio-stimulants & Growth Promoters", desc: "Amino acid hydrolysates, seaweed extracts, plant growth hormones — liquid, granular, foliar forms." },
          { name: "Trichoderma Biocontrol", desc: "Plant disease suppression — protects against fusarium, pythium and damping-off." },
          { name: "Phosphate Solubilising Bacteria (PSB)", desc: "Converts insoluble soil phosphorus into plant-available forms — reduces phosphate fertilizer dependency." },
        ],
      },
      {
        title: "🌍 Special Blends & Global-Demand Formulations",
        items: [
          { name: "Organic NPK 5-5-5 Complete Blend", desc: "Balanced organic nitrogen-phosphorus-potassium blend formulated for vegetable gardens and row crops. USDA NOP listed materials only." },
          { name: "Biostimulant + Micronutrient Combo", desc: "Seaweed extract + amino acids + chelated zinc & boron. Proven yield improvement in trials across wheat, corn, and vegetables." },
          { name: "Mycorrhizal + Humic Acid Granule", desc: "Root health package — reduces transplant shock, boosts establishment in newly planted orchards and vineyards." },
          { name: "Vermi + Neem Integrated Blend", desc: "Popular in EU and Australian organic horticulture — pest suppression + nutrient delivery in one application." },
          { name: "Seaweed + Fish Hydrolysate Liquid", desc: "Premium foliar feed for high-value crops. Used by organic citrus, berry and vineyard producers globally." },
          { name: "Carbon-Rich Biochar Blend", desc: "Biochar + compost blend for carbon sequestration and long-term soil structure improvement. Growing demand in European regenerative agriculture." },
          { name: "Potassium Humate + Fulvic Tonic", desc: "Soil activator for saline and degraded soils — reclamation blends for Middle Eastern and African desert agriculture." },
          { name: "Organic Boron + Calcium Blend", desc: "Corrects deficiencies in high-yield brassicas, stone fruits and solanaceous crops. EU Organic approved materials." },
          { name: "Custom Crop-Specific Blends", desc: "Formulated on request for rice, sugarcane, cotton, coffee, cacao and specialty crops. Lab testing + crop trial data provided." },
        ],
      },
    ],
  },
  {
    slug: "inorganic-fertilizers",
    index: "02",
    name: "Inorganic Fertilizers",
    tagline: "Mineral, precision & high-yield nutrition formulations",
    icon: "FlaskConical",
    image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=1200&q=85",
    hero: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=1920&q=90",
    stats: { countries: "32+", products: "35+" },
    blurb: "Full range of straight, NPK complex, water-soluble, controlled-release and micronutrient fertilizers — engineered for high-yield agriculture and exported with full MSDS, COA and Phytosanitary documentation. Trusted by industrial farms, plantation operators and government agriculture programs across 32+ nations.",
    highlights: [
      "MSDS, COA & Phytosanitary handled",
      "Bulk bag, 25/50kg, jumbo & liquid totes",
      "Private labelling available",
      "Nano Urea & Nano DAP — next-generation precision nutrition",
    ],
    groups: [
      {
        title: "Straight / Single Nutrient",
        items: [
          { name: "Urea (N 46%) & Neem-Coated Urea", desc: "Prilled & granular, export spec. Neem coating slows nitrogen release, reducing losses and improving efficiency." },
          { name: "Nano Urea & Nano DAP", desc: "Next-gen precision nutrition — liquid concentrate, 500ml replaces one 50kg bag. IFFCO-origin, globally patented." },
          { name: "DAP (18-46-0)", desc: "Diammonium phosphate, premium grade. Essential starter fertilizer for most field crops." },
          { name: "MOP (60% K2O)", desc: "Standard and granular MOP. Potassium source for high-yield crops and potassium-deficient soils." },
          { name: "SSP / TSP", desc: "Single & Triple Super Phosphate. Cost-effective phosphorus sources for broadcast and blend applications." },
          { name: "Ammonium Sulphate", desc: "Crystalline, 21% N with sulphur. Ideal for sulphur-deficient soils and oilseed crops." },
        ],
      },
      {
        title: "NPK Complex / Compound",
        items: [
          { name: "NPK 10-10-10 / 15-15-15 / 19-19-19 / 20-20-20", desc: "All standard balanced grades. Widely used in general cropping and plantation agriculture." },
          { name: "NPK 10-26-26 / 20-20-0 / NPKS", desc: "Specialized ratios for specific crop nutrition programmes." },
          { name: "NPK + Bio-Stimulant Blends", desc: "Chemical + organic fusion — improving nutrient efficiency and reducing environmental footprint." },
        ],
      },
      {
        title: "Specialty & Advanced",
        items: [
          { name: "Water-Soluble (Fertigation)", desc: "Drip & foliar compatible. Zero residue, high bioavailability. Designed for greenhouse and precision farming." },
          { name: "Polymer-Coated Controlled Release", desc: "30/60/90/120-day release profiles. Reduces labour and application frequency significantly." },
          { name: "Chelated Micronutrients (Zn-EDTA 12%)", desc: "Highest bioavailability in high-pH soils. EDTA, EDDHA and organic acid chelates available." },
          { name: "Micronutrient Mixes", desc: "Zn, B, Fe, Mn, Cu, Mo — single and combined formulations for deficiency correction." },
        ],
      },
      {
        title: "Crop Protection",
        items: [
          { name: "Insecticides / Fungicides / Herbicides", desc: "CIB-registered formulations. Documented compliance with destination country import restrictions." },
          { name: "Plant Growth Regulators", desc: "Gibberellins, cytokinins, auxins — for fruit setting, thinning and maturation management." },
          { name: "Mineral Sulphur & Iron Pyrites", desc: "Bulk industrial supply for soil acidification and sulphur nutrition." },
        ],
      },
    ],
  },
  {
    slug: "fruits-vegetables",
    index: "04",
    name: "Fruits & Vegetables",
    tagline: "Fresh, frozen, dried, preserved & processed produce",
    icon: "Apple",
    image: "https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800",
    hero: "https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200",
    stats: { countries: "38+", products: "55+" },
    blurb: "Alphonso mangoes, Bhagwa pomegranates, Nashik onions and Indian exotics — reefer container and air-freight fulfilment with full cold-chain traceability. India's diverse agricultural geography gives AR Overseas access to the finest seasonal produce from each micro-climate zone.",
    highlights: [
      "APEDA & FSSAI certified supply chains",
      "GrapeNet, MangoNet, AnarNet traceability",
      "GAP & GlobalG.A.P. farm networks",
      "Reefer containers and air-freight fulfilment",
      "IQF & aseptic processed options year-round",
    ],
    groups: [
      {
        title: "Fresh Vegetables",
        items: [
          { name: "Onions & Shallots", desc: "Nashik Red, Nashik White, Bangalore Rose — India's top onion export varieties with consistent sizing and shelf-life." },
          { name: "Potatoes", desc: "Table and processing grades — widely demanded by food processing, restaurant and institutional buyers." },
          { name: "Garlic", desc: "Regular & grand bold — Indian garlic prized for its strong flavour and long shelf life." },
          { name: "Tomatoes / Carrots / Turnips / Beetroot", desc: "Field-fresh, graded and sorted to destination market standards." },
          { name: "Leafy & Alliaceous Vegetables", desc: "Leeks, scallions, lettuce, chicory — air-freighted for freshness." },
          { name: "Mushrooms & Other Fresh Veg", desc: "Button, shiitake, oyster — controlled-atmosphere packaging available." },
        ],
      },
      {
        title: "Fresh Fruits & Nuts",
        items: [
          { name: "Alphonso, Kesar & Totapuri Mango", desc: "King of mangoes — VHT & irradiation-treated for maximum market access. The Alphonso Mango is among the world's most prized tropical fruits." },
          { name: "Pomegranates (Bhagwa, Ganesh)", desc: "Deep-red aril, premium export pack. India's Bhagwa pomegranate holds record shelf life and sweetness levels." },
          { name: "Grapes (Thompson, Flame, Sharad)", desc: "Nashik origin — India's premium wine & table grape region. Reefer 1°C cold chain from farm gate." },
          { name: "Bananas, Papaya, Pineapple, Guava, Avocado", desc: "Full tropical basket — India's year-round growing seasons ensure consistent supply." },
          { name: "Cashew Nuts & Other Nuts", desc: "W180, W240, W320, W450 — India is the world's largest cashew processor." },
          { name: "Coconuts (Tender & Mature)", desc: "De-husked and husked options — high demand from health beverage and food processing sectors." },
        ],
      },
      {
        title: "Processed & Preserved",
        items: [
          { name: "Mango Pulp (Alphonso/Totapuri)", desc: "Aseptic bulk 215kg drums — used by global juice, ice cream and confectionery manufacturers." },
          { name: "Frozen Fruits & Vegetables", desc: "IQF peas, corn, mango cubes, pomegranate arils — quick-frozen at harvest peak for maximum nutrition." },
          { name: "Jams, Jellies & Marmalades", desc: "Private label ready — extensive flavour range from Indian tropical and temperate fruits." },
          { name: "Fruit & Vegetable Juices", desc: "Coconut water, pomegranate, mango nectar — Tetra Pak, glass and PET packaging options." },
          { name: "Pickles & Chutneys", desc: "Traditional Indian range — growing demand in diaspora and gourmet food retail." },
          { name: "Cane Sugar, Molasses & Syrups", desc: "Raw, refined & specialty — India is among the world's largest sugar producers." },
        ],
      },
    ],
  },
  {
    slug: "construction-materials",
    index: "05",
    name: "Construction Materials",
    tagline: "Stone, ceramics, cement & mineral products",
    icon: "HardHat",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=85",
    hero: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1920&q=90",
    stats: { countries: "25+", products: "45+" },
    blurb: "Rajasthan and South-Indian quarried marble and granite, cement, ceramic tiles and industrial minerals — FOB Mundra, JNPT, Chennai & Pipavav. India's vast geological diversity makes it one of the world's leading natural stone suppliers.",
    highlights: [
      "Direct quarry partnerships — Rajasthan, Karnataka, Andhra Pradesh",
      "Gang-saw and cutter slabs, calibrated tiles & blocks",
      "CE, BS & ASTM compliant grades",
      "Morbi ceramic hub — world's second-largest tile manufacturing cluster",
    ],
    groups: [
      {
        title: "Natural Stone",
        items: [
          { name: "Marble & Travertine", desc: "Makrana, Rajnagar, Ambaji, Morwad, onyx — India's white marbles are celebrated globally for purity and translucency." },
          { name: "Granite & Porphyry", desc: "Black Galaxy, Tan Brown, Kashmir White, Imperial Red — India is the world's #2 granite exporter." },
          { name: "Sandstone & Basalt", desc: "Rainbow, Mint, Agra Red, Desert Brown — popular for landscaping, flooring and heritage restoration." },
          { name: "Limestone & Dolomite", desc: "Kota, Shahabad, Yellow, Blue — premium flooring choices for outdoor and commercial applications." },
          { name: "Quartzite, Slate, Alabaster", desc: "Cladding & roofing grades — natural alternatives to engineered stone with unique aesthetics." },
          { name: "Cobbles, Setts, Curbstones, Flagstones", desc: "Landscaping & paving — high demand in European urban redevelopment and heritage streetscape projects." },
        ],
      },
      {
        title: "Cement & Binders",
        items: [
          { name: "Portland Cement (OPC 43/53, PPC, PSC)", desc: "Bulk & bagged — India produces over 600 million tonnes annually, supporting competitive export pricing." },
          { name: "Aluminous, Slag & Supersulphate Cement", desc: "Specialty binders for extreme environments and refractory applications." },
          { name: "Gypsum, Anhydrite & Calcined Plasters", desc: "POP & drywall grades — India's Rajasthan holds vast gypsum reserves." },
          { name: "Lime & Limestone Flux", desc: "Industrial furnace-grade — essential for steel manufacturing and water treatment." },
        ],
      },
      {
        title: "Ceramics & Tiles",
        items: [
          { name: "Vitrified & Ceramic Floor Tiles", desc: "600×600 / 800×800 / 1200×600 — Morbi, Gujarat produces over 3 billion sq. metres annually at unmatched value." },
          { name: "Wall, Mosaic & Subway Tiles", desc: "Vast design range — digital printing, textured surfaces, stone-effect finishes." },
          { name: "Roofing & Chimney Tiles", desc: "Clay & fired ceramic — traditional and contemporary architectural options." },
          { name: "Refractory Bricks & Blocks", desc: "Fire, acid & basic — industrial furnace linings and high-temperature applications." },
          { name: "Ceramic Sanitary Fixtures", desc: "WCs, basins, pedestals — competitive pricing with CE and BIS certified quality." },
        ],
      },
      {
        title: "Industrial Minerals",
        items: [
          { name: "Kaolin, Feldspar, Talc, Mica", desc: "Processed & micronised — used in ceramics, paint, paper and cosmetics industries." },
          { name: "Natural Graphite, Pumice, Emery, Chalk", desc: "Abrasive & filler grades for industrial applications." },
          { name: "Natural Phosphates", desc: "Where permitted by destination." },
        ],
      },
    ],
  },
  {
    slug: "infrastructure-steel",
    index: "06",
    name: "Infrastructure & Steel",
    tagline: "Steel, wood products & heavy engineering",
    icon: "Factory",
    image: "https://images.pexels.com/photos/36003989/pexels-photo-36003989.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800",
    hero: "https://images.pexels.com/photos/36003989/pexels-photo-36003989.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200",
    stats: { countries: "20+", products: "50+" },
    blurb: "Structural steel, flat-rolled coils, stainless, aluminium profiles, wire, plywood and pre-fab wooden components — mill-direct from India's leading steel producers. India's rapidly expanding steel sector now produces over 120 million tonnes annually, making it the world's second-largest producer.",
    highlights: [
      "SAIL, JSW, Tata, Jindal origin — verified mill certificates",
      "TC + MTC + CE / ASTM documentation",
      "Containerised & break-bulk shipment options",
      "Custom fabrication and value-added processing available",
    ],
    groups: [
      {
        title: "Iron & Steel Primary",
        items: [
          { name: "Ingots, Blooms, Billets", desc: "Non-alloy & alloy — feedstock for rolling mills and re-rollers." },
          { name: "Sponge Iron (DRI)", desc: "Lumpy & briquetted — India is the world's largest DRI producer." },
          { name: "Ferrous Scrap", desc: "HMS 1&2, shredded, bonus — traded against current market indices." },
          { name: "Pig Iron & Spiegeleisen", desc: "Foundry & basic grades for casting and alloy production." },
        ],
      },
      {
        title: "Flat-Rolled & Long Steel",
        items: [
          { name: "Hot & Cold Rolled Coils / Sheets", desc: "IS/ASTM/JIS/EN specs — wide range of grades and thicknesses." },
          { name: "Galvanized / GI / GL / PPGI / PPGL", desc: "Z80–Z275 coatings — widely used in construction, automotive and appliance industries." },
          { name: "Stainless Flat & Long (304, 316, 430)", desc: "Coil, sheet, strip — India's stainless production capacity has tripled in the past decade." },
          { name: "Bars, Rods, TMT, Angles, Channels, Beams", desc: "Construction standard — TMT rebars are India's highest-volume steel export." },
          { name: "Wire, Barbed Wire, Mesh, Fencing", desc: "GI & PVC-coated — agricultural, security and construction applications." },
        ],
      },
      {
        title: "Fabricated Metal",
        items: [
          { name: "Steel Structures (Bridges, Towers, PEB)", desc: "Designed & fabricated — competitive Indian engineering at international quality standards." },
          { name: "Doors, Windows, Frames", desc: "Security & architectural — powder-coated and galvanised finishes available." },
          { name: "Tanks, Drums, Containers", desc: "Storage & transport — UN-certified hazmat options available." },
          { name: "Wire Ropes, Chains & Fittings", desc: "Industrial lifting — LEEA and EN 818 compliant." },
          { name: "Aluminium / Copper / Zinc Tubes & Profiles", desc: "Extruded & drawn — custom cross-sections on request." },
        ],
      },
      {
        title: "Wood & Panels",
        items: [
          { name: "Sawn Timber & Plywood", desc: "Marine, commercial, BWR — FSC-certified options available." },
          { name: "Laminated & Veneered Panels", desc: "MDF, particle, HPL — interior fit-out and furniture manufacturing." },
          { name: "Flooring, Joinery, Prefab Components", desc: "Ready-to-install — engineered wood flooring and modular components." },
        ],
      },
    ],
  },
  {
    slug: "live-plants-herbs",
    index: "07",
    name: "Live Plants & Herbs",
    tagline: "Live plants, medicinal herbs, botanical extracts & essential oils",
    icon: "Flower2",
    image: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=800&q=80",
    hero: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1600&q=80",
    stats: { countries: "22+", products: "80+" },
    blurb: "Ornamental trees, tropical shrubs, palms, creepers, ground-cover, medicinal herbs and essential oils — phytosanitary-cleared nursery stock. India's extraordinary botanical diversity spans tropical, sub-tropical and alpine zones, giving AR Overseas unmatched breadth of plantable and extractable material.",
    highlights: [
      "Phytosanitary + ISPM-15 certified handling",
      "Tissue-culture & field nursery supply",
      "Ayurvedic pharmacopoeia herbs",
      "Essential oils — steam-distilled and cold-pressed",
    ],
    groups: [
      {
        title: "Ornamental Trees (Botanical · Common)",
        items: [
          { name: "Azadirachta indica", desc: "Neem — multifunctional medicinal and shade tree." },
          { name: "Alstonia scholaris / Grevillea robusta", desc: "Silver Oak — fast-growing avenue and agroforestry tree." },
          { name: "Bombax ceiba", desc: "Semal — striking red-flowering tropical tree." },
          { name: "Bauhinia acuminata", desc: "White Bauhinia — ornamental flowering tree for warm climates." },
          { name: "Bauhinia blakeana", desc: "Hong Kong Orchid Tree — large, vivid flowers." },
          { name: "Cassia fistula", desc: "Amaltas (Golden Shower) — India's national flower tree." },
          { name: "Delonix regia", desc: "Gulmohar (Flame Tree) — iconic tropical landscape tree." },
          { name: "Neolamarckia cadamba", desc: "Kadamb — sacred Indian tree with aromatic flowers." },
          { name: "Chorisia speciosa", desc: "Silk Floss Tree — spectacular blooms and unique spiny trunk." },
          { name: "Erythrina indica", desc: "Indian Coral Tree — fast-growing, vivid red blooms." },
          { name: "Millingtonia hortensis", desc: "Seeta Ashok — fragrant white flowers, elegant form." },
          { name: "Mimusops elengi", desc: "Maulsari — dense evergreen, fragrant flowers." },
          { name: "Plumeria (White / Yellow / Pink / Red)", desc: "Frangipani — tropical icon, widely used in landscaping and floriculture." },
          { name: "Polyalthia longifolia", desc: "Ashok (False) — tall, columnar, ideal for avenues and boundaries." },
          { name: "Putranjiva roxburghii", desc: "Putranjiva — traditional sacred and medicinal tree." },
          { name: "Spathodea campanulata", desc: "African Tulip Tree — bold red flowers, rapidly growing tropical." },
          { name: "Tabebuia rosea / argentea", desc: "Pink & Silver Trumpet — spectacular seasonal blooming." },
          { name: "Terminalia arjuna", desc: "Arjuna — important Ayurvedic cardiac medicinal tree." },
        ],
      },
      {
        title: "Fruit Trees",
        items: [
          { name: "Mangifera indica", desc: "Mango — grafted & seedling. Multiple varieties available including Alphonso, Kesar, Langra and Dussehri." },
          { name: "Artocarpus heterophyllus", desc: "Jackfruit — world's largest tree fruit, growing demand in plant-based food industry." },
        ],
      },
      {
        title: "Shrubs",
        items: [
          { name: "Alpinia variegata", desc: "Variegated Ginger Lily." },
          { name: "Clerodendrum inerme", desc: "Hedge Clerodendrum." },
          { name: "Calliandra haematocephala", desc: "Powder Puff." },
          { name: "Canna Lily Variegated", desc: "Ornamental Canna." },
          { name: "Dracaena marginata", desc: "Dragon Tree." },
          { name: "Ficus benjamina Green Hedge", desc: "Weeping Fig." },
          { name: "Galphimia glauca", desc: "Thryallis." },
          { name: "Hamelia patens (Dwarf / Regular)", desc: "Firebush." },
          { name: "Hibiscus Dwarf", desc: "Ornamental hibiscus." },
          { name: "Hymenocallis littoralis", desc: "Spider Lily." },
          { name: "Ixora (Red / Orange)", desc: "Flame of the Woods." },
          { name: "Leucophyllum Topiary", desc: "Texas Sage." },
          { name: "Lilium lancifolium", desc: "Tiger Lily." },
          { name: "Mussaenda (Red / White / Pink)", desc: "Ornamental Mussaenda." },
          { name: "Plumeria pudica", desc: "Bridal Bouquet." },
          { name: "Poinsettia (Red / White)", desc: "Christmas Star." },
          { name: "Radermachera sinica", desc: "China Doll." },
          { name: "Syzygium myrtifolium", desc: "Australian Brush Cherry." },
          { name: "Tecoma capensis / gaudichaudi", desc: "Cape / Yellow Tecoma." },
          { name: "Tabernaemontana divaricata (Double / Variegated / Dwarf)", desc: "Chandni — green double, variegated, dwarf." },
          { name: "Seasonal Plants", desc: "As per selection/season." },
        ],
      },
      {
        title: "Palms",
        items: [
          { name: "Wodyetia bifurcata", desc: "Fox Tail Palm." },
          { name: "Caryota", desc: "Fish Tail Palm." },
          { name: "Cycas revoluta", desc: "Queen Sago." },
          { name: "Phoenix sylvestris", desc: "Date Palm." },
        ],
      },
      {
        title: "Ground Cover",
        items: [
          { name: "Pennisetum setaceum", desc: "Fountain Grass." },
          { name: "Dianella", desc: "Flax Lily." },
          { name: "Zebrina pendula", desc: "Wandering Jew." },
          { name: "Rhoeo discolor", desc: "Moses-in-the-Cradle." },
          { name: "Sphagneticola trilobata", desc: "Wedelia." },
        ],
      },
      {
        title: "Creepers",
        items: [
          { name: "Bougainvillea glabra", desc: "Paper Flower." },
          { name: "Clerodendrum splendens", desc: "Scarlet Glory Bower." },
          { name: "Quisqualis indica", desc: "Rangoon Creeper." },
          { name: "Thunbergia grandiflora", desc: "Blue Trumpet Vine." },
          { name: "Clematis (White)", desc: "Ornamental climber." },
        ],
      },
      {
        title: "Medicinal & Aromatic",
        items: [
          { name: "Ashwagandha (Withania somnifera)", desc: "Adaptogenic root — among the world's most traded Ayurvedic herbs. Growing demand from supplement manufacturers." },
          { name: "Curry Leaf (Murraya koenigii)", desc: "Culinary & medicinal — essential in South Asian cuisine globally." },
          { name: "Turmeric (Curcuma longa)", desc: "Live rhizome & dried. Curcumin content 3%+ available. Fastest-growing functional food ingredient globally." },
          { name: "Brahmi (Centella asiatica)", desc: "Cognitive herb — significant demand from nutraceutical manufacturers." },
          { name: "Aloe Vera", desc: "Nursery stock & gel — multi-billion dollar ingredient in cosmetics, food and pharma." },
          { name: "Neem (Azadirachta indica)", desc: "Saplings, leaves, cake, oil — broadest-spectrum botanical with agricultural and pharmaceutical applications." },
          { name: "Psyllium Husk (Isabgol)", desc: "Fibre supplement raw material — India supplies 85% of global psyllium demand." },
          { name: "Lemongrass, Tulsi, Ginger, Rosemary, Thyme", desc: "Culinary & essential-oil herbs — fresh and dried supply available." },
          { name: "Moringa (Drumstick)", desc: "Leaves, pods, powder — called the 'miracle tree', explosive demand in health food market." },
        ],
      },
      {
        title: "Essential Oils & Extracts",
        items: [
          { name: "Essential Oils (Terpene-less / Full)", desc: "Sandalwood, vetiver, lemongrass, tulsi, neem, eucalyptus — steam-distilled and cold-pressed." },
          { name: "Oleoresins, Resinoids, Absolutes", desc: "Spice oleoresins — pepper, ginger, turmeric. Used in food flavouring and fragrance industries." },
          { name: "Bamboo, Rattan & Reeds", desc: "Industrial & decorative — sustainable alternatives to plastics gaining global traction." },
        ],
      },
    ],
  },
  {
    slug: "textiles-apparel",
    index: "08",
    name: "Textiles & Apparel",
    tagline: "Woven fabrics, garments, home textiles & floor coverings",
    icon: "Shirt",
    image: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=800&q=80",
    hero: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=1600&q=80",
    stats: { countries: "30+", products: "40+" },
    blurb: "Premium silk, cotton, linen and synthetic wovens; menswear, womenswear, ethnic, home textiles and carpets — mill-to-market programs. India's textile industry — the second-largest employer in the country — produces fabric of unrivalled diversity, from gossamer Varanasi silk to sturdy Ludhiana knitwear.",
    highlights: [
      "OEKO-TEX, GOTS, BCI certifications available",
      "OEM / private label manufacturing",
      "Design & sampling studios in Delhi, Tirupur, Ludhiana",
      "Handloom GI products — Banarasi, Kanjivaram, Chanderi, Paithani",
    ],
    groups: [
      {
        title: "Woven Fabrics",
        items: [
          { name: "Silk Woven", desc: "Mulberry, tussar, muga, eri — India's silk heritage spans 5,000 years." },
          { name: "Cotton (85%+, <200gsm / >200gsm)", desc: "Shirting, dress, bottom weight — Indian cotton renowned for staple length and lustre." },
          { name: "Cotton Blends", desc: "Poly-cotton, cotton-viscose — performance and value engineered for different market needs." },
          { name: "Linen & Flax", desc: "Pure & blended — growing demand in sustainable fashion segment." },
          { name: "Jute & Bast Fibre", desc: "Hessian, sacking, canvas — eco-friendly alternative to synthetic packaging." },
          { name: "Synthetic & Artificial Filament", desc: "Polyester, viscose — Surat is Asia's largest synthetic textile hub." },
          { name: "Terry & Quilted Textiles", desc: "Towelling & padded — high-volume supply for retail and hospitality sectors." },
        ],
      },
      {
        title: "Menswear",
        items: [
          { name: "Overcoats, Suits, Blazers", desc: "Tailored & MTM — Indian tailoring tradition producing boardroom-quality garments." },
          { name: "Shirts (Woven & Knit)", desc: "Formal, casual, ethnic — India exports over $4 billion in shirts annually." },
          { name: "T-Shirts, Polos, Jerseys", desc: "Tirupur-origin knits — the T-shirt capital of the world." },
          { name: "Trousers, Jeans, Chinos", desc: "Denim & non-denim — competitive pricing with global-standard quality." },
          { name: "Sleepwear, Underwear, Activewear", desc: "Full category range — OEKO-TEX certified fabrics available." },
        ],
      },
      {
        title: "Womenswear",
        items: [
          { name: "Dresses, Skirts, Suits", desc: "Western wear — full design and private-label capability." },
          { name: "Blouses, Tops, Tunics", desc: "Woven & knit — hand-embroidered and machine embroidered options." },
          { name: "Sarees & Lehengas", desc: "Banarasi, Kanjivaram, Chanderi, Paithani — GI-tagged heritage textiles." },
          { name: "Salwar Kameez & Kurtas", desc: "Festive & daily wear — growing demand from global South Asian fashion boutiques." },
          { name: "Nightwear & Innerwear", desc: "Cotton & modal — expanding export to European private-label brands." },
        ],
      },
      {
        title: "Home Textiles",
        items: [
          { name: "Bed Linen & Pillow Covers", desc: "Cotton, sateen, percale — India is the world's largest cotton bed linen exporter." },
          { name: "Bath Towels", desc: "Terry, ring-spun, carded — GSM range 300–900 for retail, hospitality and healthcare." },
          { name: "Curtains, Drapes, Blinds", desc: "Custom sizes — flame-retardant and blackout options available." },
          { name: "Knotted, Tufted & Dhurrie Carpets", desc: "Bhadohi, Agra, Jaipur — hand-knotted wool and silk carpets with heritage designs." },
          { name: "Hosiery & Socks", desc: "Cotton, wool blends — Ludhiana is India's knitwear capital." },
        ],
      },
    ],
  },
  {
    slug: "metals-engineering",
    index: "09",
    name: "Metals & Engineering",
    tagline: "Non-ferrous metals, precision-engineered hardware",
    icon: "Cog",
    image: "https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?auto=format&fit=crop&w=800&q=80",
    hero: "https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?auto=format&fit=crop&w=1600&q=80",
    stats: { countries: "18+", products: "30+" },
    blurb: "Non-ferrous primary metals and precision hardware — rods, foils, profiles, fasteners and decorative hardware. India's engineering sector, valued at over $100 billion, produces precision components that compete globally on quality and cost.",
    highlights: [
      "LME-linked pricing transparency",
      "IS, ASTM, DIN, BS compliant grades",
      "Mill + fabricator direct supply",
      "Moradabad brass cluster — world's largest brass handicraft hub",
    ],
    groups: [
      {
        title: "Non-Ferrous Metals",
        items: [
          { name: "Aluminium Alloy Profiles", desc: "6063, 6061, 7075 — architectural, structural and industrial applications." },
          { name: "Aluminium Foil", desc: "Household & industrial gauges — food-grade and pharmaceutical pack options." },
          { name: "Copper Rods, Wire, Cathode", desc: "ETP & OFC — high conductivity for electrical and electronics industries." },
          { name: "Brass Fittings", desc: "Plumbing, electrical, decorative — Moradabad brass industry with 300-year heritage." },
          { name: "Zinc Ingots", desc: "SHG 99.995% — used in galvanising, die-casting and chemical industries." },
          { name: "Lead Products", desc: "Refined & alloyed — battery manufacturing and radiation shielding applications." },
        ],
      },
      {
        title: "Precision & Industrial Hardware",
        items: [
          { name: "Bolts, Nuts, Screws, Fasteners", desc: "Grade 4.6 – 12.9 — India's fastener industry exports to over 80 countries." },
          { name: "Springs & Leaf Springs", desc: "Automotive & industrial — OEM supply to leading vehicle manufacturers." },
          { name: "Castors with Metal Mounting", desc: "Trolley & furniture grade — extensive range of load ratings and wheel materials." },
          { name: "Automatic Door Closers", desc: "Hydraulic & pneumatic — architectural and commercial building applications." },
          { name: "Mirrors of Base Metal", desc: "Industrial reflectors — precision-ground for optical and industrial use." },
          { name: "Ornamental Metal Statuettes, Picture Frames", desc: "Decorative castings — handcrafted Indian metalware for global gift and home décor retail." },
        ],
      },
    ],
  },
  {
    slug: "food-products",
    index: "10",
    name: "Processed Food Products",
    tagline: "Prepared foods, cereals, spices & specialty items",
    icon: "UtensilsCrossed",
    image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=800&q=80",
    hero: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=1600&q=80",
    stats: { countries: "26+", products: "35+" },
    blurb: "Ready-to-eat meals, breakfast cereals, spices, blends, herbal teas, dietary supplements and nutraceuticals — ISO 22000, HACCP and FSSAI certified supply chains. India's spice industry alone supplies over 75% of the world's spice demand, with unmatched variety, quality and competitive pricing.",
    highlights: [
      "Private label & contract manufacturing",
      "Halal + Kosher + Vegan certified streams",
      "Aflatoxin <4ppb — third-party tested",
      "India supplies 75% of global spice demand",
    ],
    groups: [
      {
        title: "Prepared Cereals & Breakfast",
        items: [
          { name: "Puffed Rice / Breakfast Cereals", desc: "Multi-grain, sweetened, fortified — India's ready-to-eat food sector growing 15% annually." },
          { name: "Corn Flakes & Instant Oats", desc: "Retail and bulk — private label manufacturing with custom formulation." },
          { name: "Malt Extract", desc: "Food & brewery grade — high diastatic power for craft brewing and confectionery." },
          { name: "Cereal Bars", desc: "Granola, protein, fruit — India's healthy snacking export segment expanding rapidly." },
          { name: "Ready-to-Eat Meals", desc: "Indian thali retort pouches — shelf-stable 18 months, no preservatives, microwave-ready." },
        ],
      },
      {
        title: "Spices & Blends",
        items: [
          { name: "Turmeric Powder (Curcumin 3%+)", desc: "Erode & Nizamabad origin — world's most sought-after anti-inflammatory spice. ASTA colour 30+." },
          { name: "Red Chilli (Teja / Sannam / Byadgi)", desc: "ASTA 60–180 colour range — heat levels from mild to extra-hot, custom grind sizes." },
          { name: "Cumin, Coriander, Fennel, Mustard", desc: "Whole & ground — uniform sizing, low moisture, free from pesticide residues." },
          { name: "Cardamom (Green / Black)", desc: "7mm / 8mm bold — India's Idukki Green Cardamom is the world's finest." },
          { name: "Black Pepper", desc: "MG1, ASTA, 500–550g/l — Malabar pepper with unrivalled piperine content." },
          { name: "Garam Masala & Specialty Blends", desc: "Biryani, tandoori, curry — custom blending to brand specification." },
        ],
      },
      {
        title: "Nutraceutical & Specialty",
        items: [
          { name: "Herbal Teas & Infusions", desc: "Tulsi, chamomile, ashwagandha — India's wellness tea segment growing 20% YoY." },
          { name: "Protein Powders", desc: "Whey, plant, pea, soy — ISO 22000 certified production with third-party testing." },
          { name: "Dietary Supplements", desc: "Capsules, tablets, gummies — DSHEA compliant for US market; EU Novel Food assessment support." },
          { name: "Food Preparations (NEC)", desc: "Contract formulation — R&D support for custom health food product development." },
        ],
      },
    ],
  },
  {
    slug: "arts-crafts",
    index: "11",
    name: "Arts, Crafts & Collectibles",
    tagline: "Original artworks, handcrafts, antiques & collectibles",
    icon: "Palette",
    image: "https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?auto=format&fit=crop&w=1200&q=85",
    hero: "https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?auto=format&fit=crop&w=1920&q=90",
    stats: { countries: "14+", products: "25+" },
    blurb: "Handcrafted Indian heritage — Madhubani, Warli, Pattachitra, block prints, bronze casts, marble inlay and wood marquetry. India's 7,000-year craft tradition produces objects of extraordinary beauty — each piece carrying the cultural memory of its artisan cluster.",
    highlights: [
      "GI-tagged handicraft clusters — direct artisan sourcing",
      "Fair-trade certified supply chains",
      "Museum-grade packing & fumigation",
      "CITES compliance for eligible antiques",
    ],
    groups: [
      {
        title: "Original Artworks",
        items: [
          { name: "Paintings, Drawings, Pastels", desc: "Contemporary & folk — Madhubani, Warli, Pattachitra. Each work is unique, artist-signed with provenance." },
          { name: "Original Engravings & Lithographs", desc: "Limited editions — collector-grade with certificate of authenticity." },
          { name: "Sculptures & Statuary", desc: "Bronze, marble, wood — traditional iconography and contemporary forms." },
          { name: "Mosaics & Decorative Plaques", desc: "Marble inlay, pietra dura — Agra's marble inlay tradition spans 400 years." },
          { name: "Miniature Paintings", desc: "Rajasthani, Mughal, Tanjore — exquisite detail in gold leaf, natural pigments." },
          { name: "Block Prints", desc: "Sanganeri, Bagru, Ajrakh — UNESCO-recognised hand-block printing traditions." },
        ],
      },
      {
        title: "Handicrafts & Wood",
        items: [
          { name: "Wood Marquetry & Inlaid Wood", desc: "Saharanpur craft — detailed inlay with ivory substitutes, bone and brass." },
          { name: "Carved Wood Statuettes", desc: "Sandal, rosewood — aromatic and beautiful, GI-protected crafts." },
          { name: "Jewellery & Cutlery Caskets", desc: "Kashmiri papier-mâché & wood — painted with traditional natural pigments." },
          { name: "Brass & Bronze Handicrafts", desc: "Moradabad metalware — world's largest brass handicraft exporting cluster." },
          { name: "Stone Carvings", desc: "Marble, soapstone, granite — architectural elements and decorative objects." },
          { name: "Handmade Jewellery", desc: "Silver, oxidised, beaded — Rajasthani, tribal and contemporary designs." },
        ],
      },
      {
        title: "Antiques & Collectibles",
        items: [
          { name: "Antiques (100+ years)", desc: "With provenance certificates — CITES compliant, ASI clearance where required." },
          { name: "Ethnographic & Historical Pieces", desc: "Textiles, tribal art — careful documentation and export permit compliance." },
          { name: "Numismatic & Mineralogical", desc: "Collectors grade — coins, gems, minerals with grading certificates." },
          { name: "Vintage Textiles", desc: "Kantha, block print, brocade — museum-quality storage and conservation packaging." },
        ],
      },
    ],
  },
];

export const getSector = (slug) => sectors.find((s) => s.slug === slug);

export const countries = [
  "UAE", "Saudi Arabia", "Bahrain", "Qatar", "Kuwait", "Oman", "Jordan", "Lebanon", "Israel", "Yemen", "Iraq", "Iran",
  "Algeria", "Egypt", "Morocco", "Libya", "Tunisia", "Nigeria", "Senegal", "Cameroon", "Benin", "Ivory Coast", "Togo", "Guinea",
  "Germany", "Netherlands", "Belgium", "United Kingdom", "Ireland", "Spain", "France", "Cyprus", "Ukraine",
  "Bangladesh", "Sri Lanka", "Malaysia", "Singapore", "Maldives", "Vietnam", "Indonesia", "Thailand", "China", "Turkey",
  "United States", "Canada", "Brazil", "Colombia",
  "Australia", "Swaziland", "Syria", "Palestine",
];

export const certifications = [
  { code: "APEDA", label: "Agri & Processed Food Export Authority" },
  { code: "ISO 22000", label: "Food Safety Management System" },
  { code: "HACCP", label: "Hazard Analysis Critical Control Points" },
  { code: "GI Tagged", label: "Geographical Indication Products" },
  { code: "GlobalG.A.P.", label: "Good Agricultural Practice Farms" },
  { code: "EU Organic", label: "EC 848/2018 Compliant Streams" },
  { code: "Halal", label: "Certified Halal Processing Lines" },
  { code: "Phyto", label: "Phytosanitary Certificate — All Shipments" },
];

export const internationalCertifications = {
  eu: [
    { code: "EC 396/2005", desc: "Maximum Residue Levels — pesticides in food & feed. All AR Overseas agricultural exports are tested to EU MRL standards." },
    { code: "EC 853/2004", desc: "Specific hygiene rules for food of animal origin — applied to all relevant product categories." },
    { code: "EU Organic 848/2018", desc: "European Union Organic production & labelling — certified organic streams available for most agricultural products." },
    { code: "CE Marking", desc: "Construction materials, steel & engineering products conform to relevant European harmonised standards." },
    { code: "EN Standards", desc: "Steel, ceramics and construction materials supplied to applicable EN specifications on request." },
  ],
  us: [
    { code: "USDA NOP", desc: "National Organic Program — organic rice, produce & herbs certified. Third-party inspection certificates available." },
    { code: "FDA FSMA", desc: "Food Safety Modernisation Act compliance — preventive controls, supplier verification and traceability records maintained." },
    { code: "ASTM", desc: "Materials & engineering specifications — steel, stone and industrial products supplied to ASTM standards." },
  ],
  gcc: [
    { code: "GSO Standards", desc: "Gulf Standardisation Organisation food safety & product standards." },
    { code: "Halal GCC", desc: "Halal certification accepted across all 6 GCC nations — applied to food, beverages and supplements." },
    { code: "UAE.S / SASO", desc: "UAE & Saudi product conformity certifications — pre-clearance documentation handled." },
    { code: "GCC.S", desc: "Gulf Cooperation Council standards compliance for all applicable product categories." },
  ],
  asia: [
    { code: "GACC", desc: "General Administration of Customs China — food import registration. Pre-registered facilities available." },
    { code: "JAS", desc: "Japanese Agricultural Standard compliance for organic and specialty products." },
    { code: "FSANZ", desc: "Food Standards Australia New Zealand — label review and compliance support provided." },
    { code: "SFA Singapore", desc: "Singapore Food Agency import requirements — pre-vetted supply chains." },
  ],
  restricted: [
    { code: "MEP Rice", desc: "Minimum Export Price compliance — all rice varieties exported at or above Indian government-mandated minimum prices." },
    { code: "CITES", desc: "Convention on International Trade of Endangered Species — compliance documentation for live plants and natural materials." },
    { code: "ICAR / NBPGR", desc: "Seeds & planting material export authorisation from Indian Council of Agricultural Research." },
    { code: "AERB / Atomic", desc: "Regulatory compliance for minerals and materials with trace radioactive content where applicable." },
  ],
};

export const packagingInfo = [
  {
    market: "EU & Germany",
    req: "Biodegradable / compostable packaging mandatory for certain food categories. FSC-certified paper and cardboard. Extended Producer Responsibility (EPR) compliance. No single-use plastic bags for retail food. Recyclable and reusable packaging preferred.",
  },
  {
    market: "GCC (UAE, Saudi, Qatar)",
    req: "Arabic labelling on outer carton. Halal-certified packaging inks for food products. Oxygen-barrier inner bags for dry food. Minimum 12-month shelf-life from production date. SASO and UAE.S compliance markings.",
  },
  {
    market: "USA / Canada",
    req: "USDA / FDA label compliance. Country of Origin (COOL) declaration. Bilingual English-French labelling for Canada. Child-resistant closures where required by CPSC. FSMA foreign supplier verification records.",
  },
  {
    market: "Australia / NZ",
    req: "FSANZ label standards — mandatory nutrient panel. Biosecurity-approved wood packaging (ISPM-15 heat-treated). Strict net-weight declaration. Country of origin labelling requirements.",
  },
  {
    market: "UK Post-Brexit",
    req: "UKCA marking for applicable products. UK-specific labelling separate from EU requirements. GB importer address mandatory on all food products. UK Conformity Assessed documentation.",
  },
  {
    market: "All Markets",
    req: "Jute, PP woven, vacuum-sealed, carton, jumbo bags & drum options. Private label / OEM available. ISPM-15 certified wood packaging. Fumigation certificates on request. Custom printing in any language. Biodegradable inner packaging available across all product lines.",
  },
];
